/** Set by atw-hub-apple WKUserScript at document start (`macos` | `ios`). */
declare global {
  interface Window {
    __ATW_HUB_SHELL__?: "macos" | "ios" | string;
    webkit?: {
      messageHandlers?: {
        hubSaveFile?: {
          postMessage: (body: {
            filename: string;
            mimeType?: string;
            base64: string;
          }) => void;
        };
        /** iOS shell: in-app Safari sheet (Done, Share, Open in Safari). */
        hubOpenURL?: {
          postMessage: (body: { url: string }) => void;
        };
        /** iOS shell: Quick Look (Done, Share, Open in another app). */
        hubPreviewFile?: {
          postMessage: (body: {
            filename: string;
            mimeType?: string;
            base64: string;
          }) => void;
        };
      };
    };
  }
}

/** True when Hub runs inside the native Mac/iOS shell (WKWebView), not a normal browser tab. */
export function isHubNativeShell(): boolean {
  if (typeof window === "undefined") return false;
  const tag = window.__ATW_HUB_SHELL__;
  return tag === "macos" || tag === "ios";
}

/** iPhone/iPad Hub app. External pages and documents must not replace this web view. */
export function isHubIosShell(): boolean {
  if (typeof window === "undefined") return false;
  return window.__ATW_HUB_SHELL__ === "ios";
}

function messageHandler(
  name: "hubSaveFile" | "hubOpenURL" | "hubPreviewFile",
): { postMessage: (body: unknown) => void } | undefined {
  try {
    return window.webkit?.messageHandlers?.[name] as { postMessage: (body: unknown) => void } | undefined;
  } catch {
    return undefined;
  }
}

/** Tip 213: Mac WKWebView ignores &lt;a download&gt;; route bytes to NSSavePanel. */
export function canNativeSaveFile(): boolean {
  if (!isHubNativeShell()) return false;
  try {
    return typeof window.webkit?.messageHandlers?.hubSaveFile?.postMessage === "function";
  } catch {
    return false;
  }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function nativeSaveFile(payload: {
  filename: string;
  mimeType?: string;
  base64: string;
}): boolean {
  if (!canNativeSaveFile()) return false;
  window.webkit!.messageHandlers!.hubSaveFile!.postMessage({
    filename: payload.filename || "attachment",
    mimeType: payload.mimeType || "application/octet-stream",
    base64: payload.base64,
  });
  return true;
}

/** True when this iOS build can present Safari for an http(s) link. */
export function canNativeOpenURL(): boolean {
  return typeof messageHandler("hubOpenURL")?.postMessage === "function";
}

/** Present the system in-app browser. Returns false on older builds with no bridge. */
export function nativeOpenURL(url: string): boolean {
  const post = messageHandler("hubOpenURL")?.postMessage;
  if (typeof post !== "function") return false;
  let href = "";
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    href = parsed.href;
  } catch {
    return false;
  }
  post({ url: href });
  return true;
}

/** True when this iOS build can show a file in Quick Look. */
export function canNativePreviewFile(): boolean {
  return typeof messageHandler("hubPreviewFile")?.postMessage === "function";
}

/** Hand a file to Quick Look (close, share, open in another app). */
export function nativePreviewFile(payload: {
  filename: string;
  mimeType?: string;
  base64: string;
}): boolean {
  const post = messageHandler("hubPreviewFile")?.postMessage;
  if (typeof post !== "function") return false;
  post({
    filename: payload.filename || "attachment",
    mimeType: payload.mimeType || "application/octet-stream",
    base64: payload.base64,
  });
  return true;
}
